import React, { useState, useEffect, useRef } from 'react'
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Pressable,
  ScrollView,
  Dimensions,
  SafeAreaView,
  KeyboardAvoidingView,
  Platform,
  useWindowDimensions
} from 'react-native'
import { Feather } from '@expo/vector-icons'
import moment from 'moment'
import { colorPalette, fontPalette } from '../../../static/styles/global'

export interface CustomDatePickerModalProps {
  visible: boolean
  value?: Date
  date?: Date
  minimumDate?: Date
  maximumDate?: Date
  onConfirm: (selectedDate: Date) => void
  onCancel: () => void
  testID?: string
}

export const CustomDatePickerModal: React.FC<CustomDatePickerModalProps> = ({
  visible,
  value,
  date,
  minimumDate,
  maximumDate,
  onConfirm,
  onCancel
}) => {
  const { width: DEVICE_WIDTH } = useWindowDimensions()
  const initialDate = value || date || new Date()
  const [selectedDate, setSelectedDate] = useState<Date>(initialDate)
  const [currentMonth, setCurrentMonth] = useState<Date>(initialDate)
  const [viewMode, setViewMode] = useState<'calendar' | 'year'>('calendar')
  const yearScrollRef = useRef<ScrollView>(null)

  useEffect(() => {
    if (visible) {
      const d = value || date || new Date()
      setSelectedDate(d)
      setCurrentMonth(new Date(d.getFullYear(), d.getMonth(), 1))
      setViewMode('calendar')
    }
  }, [visible, value, date])

  const minYear = minimumDate ? minimumDate.getFullYear() : 1940
  const maxYear = maximumDate ? maximumDate.getFullYear() : 2050

  const years: number[] = []
  for (let y = minYear; y <= maxYear; y++) {
    years.push(y)
  }

  useEffect(() => {
    if (viewMode === 'year' && yearScrollRef.current) {
      const selectedYear = selectedDate.getFullYear()
      const index = years.indexOf(selectedYear)
      if (index > 0) {
        setTimeout(() => {
          yearScrollRef.current?.scrollTo({
            y: Math.max(0, (index - 2) * 44),
            animated: false
          })
        }, 50)
      }
    }
  }, [viewMode])

  const prevMonth = () => {
    const newMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() - 1,
      1
    )
    if (minimumDate) {
      const minMonth = new Date(
        minimumDate.getFullYear(),
        minimumDate.getMonth(),
        1
      )
      if (newMonth < minMonth) return
    }
    setCurrentMonth(newMonth)
  }

  const nextMonth = () => {
    const newMonth = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() + 1,
      1
    )
    if (maximumDate) {
      const maxMonth = new Date(
        maximumDate.getFullYear(),
        maximumDate.getMonth(),
        1
      )
      if (newMonth > maxMonth) return
    }
    setCurrentMonth(newMonth)
  }

  const isPrevDisabled = () => {
    if (!minimumDate) return false
    const minMonth = new Date(
      minimumDate.getFullYear(),
      minimumDate.getMonth(),
      1
    )
    return currentMonth <= minMonth
  }

  const isNextDisabled = () => {
    if (!maximumDate) return false
    const maxMonth = new Date(
      maximumDate.getFullYear(),
      maximumDate.getMonth(),
      1
    )
    return currentMonth >= maxMonth
  }

  const handleSelectDay = (day: number) => {
    const newDate = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day,
      selectedDate.getHours(),
      selectedDate.getMinutes(),
      selectedDate.getSeconds()
    )
    setSelectedDate(newDate)
  }

  const handleSelectYear = (year: number) => {
    const newSelected = new Date(
      year,
      selectedDate.getMonth(),
      selectedDate.getDate(),
      selectedDate.getHours(),
      selectedDate.getMinutes(),
      selectedDate.getSeconds()
    )
    setSelectedDate(newSelected)
    setCurrentMonth(new Date(year, selectedDate.getMonth(), 1))
    setViewMode('calendar')
  }

  const daysInMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth() + 1,
    0
  ).getDate()
  const firstDayOfWeek = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth(),
    1
  ).getDay()

  const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

  const isDayDisabled = (day: number) => {
    const d = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day)
    if (minimumDate) {
      const min = new Date(
        minimumDate.getFullYear(),
        minimumDate.getMonth(),
        minimumDate.getDate()
      )
      if (d < min) return true
    }
    if (maximumDate) {
      const max = new Date(
        maximumDate.getFullYear(),
        maximumDate.getMonth(),
        maximumDate.getDate()
      )
      if (d > max) return true
    }
    return false
  }

  const isDaySelected = (day: number) => {
    return (
      selectedDate.getFullYear() === currentMonth.getFullYear() &&
      selectedDate.getMonth() === currentMonth.getMonth() &&
      selectedDate.getDate() === day
    )
  }

  if (!visible) return null

  return (
    <Modal
      transparent
      animationType="fade"
      visible={visible}
      onRequestClose={onCancel}
    >
      <SafeAreaView style={{ flexGrow: 1 }}>
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={[{ flex: 1 }, DEVICE_WIDTH > 960 && styles.modalLargeScreen]}
        >
          <View style={styles.overlay}>
            <Pressable style={styles.backdrop} onPress={onCancel} />
            <View style={styles.dialogContainer}>
              {/* Header */}
              <View style={styles.header}>
                <TouchableOpacity
                  activeOpacity={0.7}
                  onPress={() => setViewMode(viewMode === 'year' ? 'calendar' : 'year')}
                >
                  <Text
                    style={[
                      styles.headerYear,
                      viewMode === 'year' && styles.headerActiveText
                    ]}
                  >
                    {moment(selectedDate).format('YYYY')}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  activeOpacity={0.7}
                  onPress={() => setViewMode('calendar')}
                >
                  <Text
                    style={[
                      styles.headerDate,
                      viewMode === 'calendar' && styles.headerActiveText
                    ]}
                  >
                    {moment(selectedDate).format('ddd, MMM D')}
                  </Text>
                </TouchableOpacity>
              </View>

              {/* Body */}
              {viewMode === 'year' ? (
                <View style={styles.yearContainer}>
                  <ScrollView
                    ref={yearScrollRef}
                    showsVerticalScrollIndicator={false}
                    contentContainerStyle={styles.yearScrollContent}
                  >
                    {years.map((y) => {
                      const isCurrentYear = selectedDate.getFullYear() === y
                      return (
                        <TouchableOpacity
                          key={y}
                          style={[
                            styles.yearItem,
                            isCurrentYear && styles.yearItemSelected
                          ]}
                          onPress={() => handleSelectYear(y)}
                        >
                          <Text
                            style={[
                              styles.yearItemText,
                              isCurrentYear && styles.yearItemTextSelected
                            ]}
                          >
                            {y}
                          </Text>
                        </TouchableOpacity>
                      )
                    })}
                  </ScrollView>
                </View>
              ) : (
                <View style={styles.calendarContainer}>
                  {/* Month Navigation */}
                  <View style={styles.monthNavRow}>
                    <TouchableOpacity
                      onPress={prevMonth}
                      disabled={isPrevDisabled()}
                      style={styles.navArrow}
                      hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                    >
                      <Feather
                        name="chevron-left"
                        size={22}
                        color={isPrevDisabled() ? '#CCCCCC' : '#424242'}
                      />
                    </TouchableOpacity>

                    <TouchableOpacity
                      onPress={() => setViewMode('year')}
                      style={styles.monthTitleWrapper}
                    >
                      <Text style={styles.monthTitle}>
                        {moment(currentMonth).format('MMMM YYYY')}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      onPress={nextMonth}
                      disabled={isNextDisabled()}
                      style={styles.navArrow}
                      hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                    >
                      <Feather
                        name="chevron-right"
                        size={22}
                        color={isNextDisabled() ? '#CCCCCC' : '#424242'}
                      />
                    </TouchableOpacity>
                  </View>

                  {/* Weekday Headers */}
                  <View style={styles.weekDaysRow}>
                    {weekDays.map((wd, index) => (
                      <View key={index} style={styles.weekDayCell}>
                        <Text style={styles.weekDayText}>{wd}</Text>
                      </View>
                    ))}
                  </View>

                  {/* Days Grid */}
                  <View style={styles.daysGrid}>
                    {/* Empty cells before first day */}
                    {Array.from({ length: firstDayOfWeek }).map((_, index) => (
                      <View key={`empty-${index}`} style={styles.dayCell} />
                    ))}

                    {/* Day cells */}
                    {Array.from({ length: daysInMonth }).map((_, index) => {
                      const day = index + 1
                      const selected = isDaySelected(day)
                      const disabled = isDayDisabled(day)

                      return (
                        <TouchableOpacity
                          key={`day-${day}`}
                          style={styles.dayCell}
                          disabled={disabled}
                          onPress={() => handleSelectDay(day)}
                        >
                          <View
                            style={[
                              styles.dayCircle,
                              selected && styles.dayCircleSelected
                            ]}
                          >
                            <Text
                              style={[
                                styles.dayText,
                                disabled && styles.dayTextDisabled,
                                selected && styles.dayTextSelected
                              ]}
                            >
                              {day}
                            </Text>
                          </View>
                        </TouchableOpacity>
                      )
                    })}
                  </View>
                </View>
              )}

              {/* Action Buttons */}
              <View style={styles.actionsRow}>
                <TouchableOpacity
                  onPress={onCancel}
                  style={styles.actionButton}
                  activeOpacity={0.6}
                >
                  <Text style={styles.cancelButtonText}>CANCEL</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  onPress={() => onConfirm(selectedDate)}
                  style={styles.actionButton}
                  activeOpacity={0.6}
                >
                  <Text style={styles.okButtonText}>OK</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </Modal>
  )
}

const styles = StyleSheet.create({
  modalLargeScreen: {
    maxWidth: '50%',
    right: 0,
    position: 'absolute',
    width: '100%',
    height: '100%'
  },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject
  },
  dialogContainer: {
    width: Math.min(340, Dimensions.get('window').width - 48),
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    overflow: 'hidden',
    elevation: 24,
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.3,
    shadowRadius: 15
  },
  header: {
    backgroundColor: '#0B495C',
    paddingHorizontal: 22,
    paddingTop: 18,
    paddingBottom: 18
  },
  headerYear: {
    fontSize: 15,
    fontFamily: fontPalette.primarySemiBold,
    color: 'rgba(255, 255, 255, 0.75)',
    marginBottom: 4
  },
  headerDate: {
    fontSize: 26,
    fontFamily: fontPalette.primaryBold,
    fontWeight: '700',
    color: '#FFFFFF'
  },
  headerActiveText: {
    color: '#FFFFFF'
  },
  calendarContainer: {
    paddingHorizontal: 12,
    paddingTop: 12,
    paddingBottom: 4
  },
  monthNavRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 8,
    paddingVertical: 6,
    marginBottom: 4
  },
  monthTitleWrapper: {
    paddingHorizontal: 10,
    paddingVertical: 4
  },
  monthTitle: {
    fontSize: 14,
    fontFamily: fontPalette.primaryBold,
    fontWeight: '700',
    color: '#212121'
  },
  navArrow: {
    padding: 6,
    borderRadius: 20
  },
  weekDaysRow: {
    flexDirection: 'row',
    marginBottom: 4
  },
  weekDayCell: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    height: 28
  },
  weekDayText: {
    fontSize: 12,
    fontFamily: fontPalette.primarySemiBold,
    color: '#9E9E9E'
  },
  daysGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap'
  },
  dayCell: {
    width: '14.28%',
    height: 38,
    alignItems: 'center',
    justifyContent: 'center'
  },
  dayCircle: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center'
  },
  dayCircleSelected: {
    backgroundColor: '#0B495C'
  },
  dayText: {
    fontSize: 13,
    fontFamily: fontPalette.primaryRegular,
    color: '#212121'
  },
  dayTextDisabled: {
    color: '#D0D0D0'
  },
  dayTextSelected: {
    color: '#FFFFFF',
    fontFamily: fontPalette.primaryBold,
    fontWeight: '700'
  },
  yearContainer: {
    height: 260,
    paddingVertical: 8
  },
  yearScrollContent: {
    paddingVertical: 10,
    alignItems: 'center'
  },
  yearItem: {
    paddingVertical: 10,
    paddingHorizontal: 24,
    borderRadius: 20,
    marginVertical: 2
  },
  yearItemSelected: {
    backgroundColor: '#0B495C'
  },
  yearItemText: {
    fontSize: 17,
    fontFamily: fontPalette.primaryRegular,
    color: '#424242'
  },
  yearItemTextSelected: {
    fontSize: 20,
    fontFamily: fontPalette.primaryBold,
    fontWeight: '700',
    color: '#FFFFFF'
  },
  actionsRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 14,
    gap: 8
  },
  actionButton: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 4
  },
  cancelButtonText: {
    fontSize: 14,
    fontFamily: fontPalette.primaryBold,
    fontWeight: '700',
    color: '#D81616',
    letterSpacing: 0.5
  },
  okButtonText: {
    fontSize: 14,
    fontFamily: fontPalette.primaryBold,
    fontWeight: '700',
    color: '#9DD086',
    letterSpacing: 0.5
  }
})

export default CustomDatePickerModal
